import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Hash,
  Flag,
  Send as SendIcon,
  X,
  AlertCircle,
  Reply,
  Trash2,
  Radio,
  Store,
  Crown,
  LifeBuoy,
  UserPlus,
  Paperclip,
  ImagePlus,
} from "lucide-react";

export function StaffAttachment({ url }) {
  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
  const isImage = /\.(png|jpe?g|gif|webp|heic|heif)(\?|$)/i.test(url);
  const isVideo = /\.(mp4|mov|webm|ogg)(\?|$)/i.test(url);
  if (isImage) {
    return (
      <a href={fullUrl} target="_blank" rel="noreferrer" className="block mt-2 max-w-[260px]" data-testid="staff-msg-image">
        <img src={fullUrl} alt="attachment" className="rounded-lg max-h-60 object-cover border border-white/10" />
      </a>
    );
  }
  if (isVideo) {
    return (
      <video src={fullUrl} controls className="mt-2 max-w-[280px] max-h-60 rounded-lg border border-white/10" data-testid="staff-msg-video" />
    );
  }
  const filename = url.split("/").pop() || "file";
  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noreferrer"
      className="mt-2 inline-flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1.5 text-xs text-cyan-200"
      data-testid="staff-msg-file"
    >
      <Paperclip className="w-3.5 h-3.5" />
      <span className="truncate max-w-[200px]">{filename}</span>
    </a>
  );
}

export function StaffPortalTab({ unread, onUnreadChange, isCEO }) {
  const { user: me } = useAuth();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [attachment, setAttachment] = useState(null); // {url, filename, content_type}
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newLevel, setNewLevel] = useState(0);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/staff/channels");
      const list = data.channels || [];
      setChannels(list);
      setActiveId((cur) => {
        if (cur && list.find((c) => c.channel_id === cur)) return cur;
        // Pick channel from URL if any
        const params = new URLSearchParams(window.location.search);
        const cid = params.get("channel");
        if (cid && list.find((c) => c.channel_id === cid)) return cid;
        return list[0]?.channel_id || null;
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load channels");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/staff/channels/${cid}/messages`, { params: { limit: 100 } });
      setMessages(data.messages || []);
      // Mark read on open
      await api.post(`/staff/channels/${cid}/read`);
      // Refresh unread badge
      try {
        const { data: u } = await api.get("/staff/unread-summary");
        onUnreadChange?.(u);
      } catch { /* no-op */ }
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error("Couldn't load messages");
      }
    }
  }, [onUnreadChange]);

  useEffect(() => { loadChannels(); }, [loadChannels]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  // Live polling — every 8s while a channel is open
  useEffect(() => {
    if (!activeId) return undefined;
    const i = setInterval(() => loadMessages(activeId), 8000);
    return () => clearInterval(i);
  }, [activeId, loadMessages]);

  // Refresh channel list (last_message / unread) every 20s
  useEffect(() => {
    const i = setInterval(loadChannels, 20000);
    return () => clearInterval(i);
  }, [loadChannels]);

  // Auto-scroll to latest
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, activeId]);

  const activeChannel = useMemo(() => channels.find((c) => c.channel_id === activeId), [channels, activeId]);

  const send = async () => {
    if (!activeId || (!body.trim() && !attachment)) return;
    try {
      await api.post(`/staff/channels/${activeId}/messages`, {
        body: body.trim(),
        reply_to: replyTo?.message_id || null,
        attachment_url: attachment?.url || null,
      });
      setBody("");
      setReplyTo(null);
      setAttachment(null);
      await loadMessages(activeId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't send");
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking same file
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      return toast.error("File too big (25 MB max for staff chat)");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({
        url: data.url,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
      });
      toast.success("Attached · hit send");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteMsg = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/staff/messages/${m.message_id}`);
      await loadMessages(activeId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't delete");
    }
  };

  const createChannel = async () => {
    const nm = (newName || "").trim();
    if (!nm) return toast.error("Channel name required");
    try {
      const { data } = await api.post("/staff/channels", {
        name: nm,
        description: newDesc,
        min_role_level: Number(newLevel) || 0,
      });
      toast.success(`#${data.name} created`);
      setShowNew(false);
      setNewName(""); setNewDesc(""); setNewLevel(0);
      await loadChannels();
      setActiveId(data.channel_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't create channel");
    }
  };

  if (loading) return <p className="text-center text-zinc-500 py-8">Loading staff portal…</p>;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="staff-portal-tab">
      <div className="grid grid-cols-[140px_1fr] sm:grid-cols-[220px_1fr] min-h-[560px]">
        {/* Channel list */}
        <div className="border-r border-white/10 bg-black/30 flex flex-col">
          <div className="p-3 flex items-center justify-between gap-2 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-black">Channels</p>
            {isCEO && (
              <button
                onClick={() => setShowNew((s) => !s)}
                className="w-6 h-6 rounded-full bg-pink-500/20 hover:bg-pink-500/30 text-pink-200 border border-pink-400/30 grid place-items-center"
                data-testid="staff-channel-new-btn"
                title="Create channel"
              >
                {showNew ? <X className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
              </button>
            )}
          </div>
          {showNew && isCEO && (
            <div className="p-3 space-y-2 border-b border-white/10 bg-pink-500/[0.06]" data-testid="staff-channel-create-form">
              <Input
                placeholder="channel-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="h-8 text-xs bg-white/5 border-white/10 rounded-lg"
                data-testid="staff-channel-name-input"
              />
              <Input
                placeholder="Description (optional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                className="h-8 text-xs bg-white/5 border-white/10 rounded-lg"
                data-testid="staff-channel-desc-input"
              />
              <select
                value={newLevel}
                onChange={(e) => setNewLevel(e.target.value)}
                className="h-8 w-full text-xs bg-zinc-900 border border-white/10 rounded-lg px-2 text-zinc-200"
                data-testid="staff-channel-min-level"
              >
                <option value={0}>Any staff (0)</option>
                <option value={1}>Support+ (1)</option>
                <option value={2}>Moderator+ (2)</option>
                <option value={3}>Admin+ (3)</option>
                <option value={4}>Senior+ (4)</option>
                <option value={5}>CEO only (5)</option>
              </select>
              <Button
                onClick={createChannel}
                className="w-full h-8 text-xs rounded-lg bg-pink-500/20 hover:bg-pink-500/30 text-pink-100 border border-pink-400/40"
                data-testid="staff-channel-create-confirm"
              >
                Create
              </Button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {channels.map((c) => {
              const isActive = c.channel_id === activeId;
              const u = (unread.per_channel || {})[c.channel_id] || 0;
              return (
                <button
                  key={c.channel_id}
                  onClick={() => setActiveId(c.channel_id)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-2 border-l-2 transition-colors ${
                    isActive
                      ? "bg-pink-500/10 border-pink-400 text-pink-100"
                      : "border-transparent text-zinc-300 hover:bg-white/5"
                  }`}
                  data-testid={`staff-channel-${c.key}`}
                >
                  {c.key === "urgent" ? <AlertCircle className="w-3.5 h-3.5 text-red-300 shrink-0" /> :
                   c.key === "reports" ? <Flag className="w-3.5 h-3.5 text-amber-300 shrink-0" /> :
                   c.key === "support" ? <LifeBuoy className="w-3.5 h-3.5 text-cyan-300 shrink-0" /> :
                   c.key === "vendors" ? <Store className="w-3.5 h-3.5 text-emerald-300 shrink-0" /> :
                   c.key === "livestreams" ? <Radio className="w-3.5 h-3.5 text-pink-300 shrink-0" /> :
                   c.key === "leadership" ? <Crown className="w-3.5 h-3.5 text-amber-300 shrink-0" /> :
                   <Hash className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
                  <span className="flex-1 text-xs font-bold truncate">{c.name}</span>
                  {u > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-black grid place-items-center" data-testid={`staff-channel-unread-${c.key}`}>
                      {u > 99 ? "99+" : u}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Message panel */}
        <div className="flex flex-col h-[560px]">
          {activeChannel ? (
            <>
              <div className="px-4 py-3 border-b border-white/10 bg-black/20">
                <p className="font-black tracking-tight inline-flex items-center gap-1.5" data-testid="staff-channel-title">
                  <Hash className="w-4 h-4 text-pink-300" /> {activeChannel.name}
                  {activeChannel.min_role_level > 0 && (
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-400/30 font-bold">
                      Level {activeChannel.min_role_level}+
                    </span>
                  )}
                </p>
                {activeChannel.description && (
                  <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{activeChannel.description}</p>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="staff-messages-scroll">
                {messages.length === 0 && (
                  <p className="text-center py-8 text-zinc-500 text-sm">No messages yet · be the first to say hi 👋</p>
                )}
                {messages.map((m) => {
                  const isMe = m.sender_id === me?.user_id;
                  const replyMsg = m.reply_to ? messages.find((x) => x.message_id === m.reply_to) : null;
                  return (
                    <div
                      key={m.message_id}
                      className={`group rounded-xl p-2.5 border ${
                        isMe ? "bg-pink-500/[0.07] border-pink-400/20" : "bg-white/[0.04] border-white/10"
                      }`}
                      data-testid={`staff-msg-${m.message_id}`}
                    >
                      <div className="flex items-baseline gap-2 mb-1">
                        <p className="text-xs font-black text-pink-200 truncate">{m.sender_name || "Staff"}</p>
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/10 text-zinc-300 font-bold">
                          {(m.sender_role || "").replace("_", " ")}
                        </span>
                        <span className="text-[10px] text-zinc-500 ml-auto whitespace-nowrap">
                          {new Date(m.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                        </span>
                      </div>
                      {replyMsg && (
                        <div className="mb-1.5 pl-2 border-l-2 border-pink-400/40 text-[11px] text-zinc-400">
                          ↪ <span className="text-pink-300">{replyMsg.sender_name}</span>: {replyMsg.body?.slice(0, 80)}
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words leading-snug">{m.body || (!m.attachment_url && <em className="text-zinc-500">(deleted)</em>)}</p>
                      {m.attachment_url && (
                        <StaffAttachment url={m.attachment_url} />
                      )}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 mt-1.5">
                        <button
                          onClick={() => setReplyTo(m)}
                          className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-pink-300 font-bold inline-flex items-center gap-1"
                          data-testid={`staff-msg-reply-${m.message_id}`}
                        >
                          <Reply className="w-3 h-3" /> Reply
                        </button>
                        {(isMe || isCEO) && (
                          <button
                            onClick={() => deleteMsg(m)}
                            className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-red-300 font-bold inline-flex items-center gap-1"
                            data-testid={`staff-msg-delete-${m.message_id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {replyTo && (
                <div className="px-3 py-1.5 bg-pink-500/[0.06] border-t border-pink-400/20 text-[11px] text-zinc-300 flex items-center gap-2">
                  <Reply className="w-3 h-3 text-pink-300" />
                  Replying to <span className="text-pink-300 font-bold">{replyTo.sender_name}</span>
                  <button onClick={() => setReplyTo(null)} className="ml-auto text-zinc-400 hover:text-white" data-testid="staff-cancel-reply">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {attachment && (
                <div className="px-3 py-2 bg-cyan-500/[0.08] border-t border-cyan-400/20 flex items-center gap-2" data-testid="staff-attachment-preview">
                  {attachment.content_type?.startsWith("image/") ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${attachment.url}`} alt={attachment.filename} className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 grid place-items-center text-cyan-200">
                      <Paperclip className="w-4 h-4" />
                    </div>
                  )}
                  <p className="text-xs text-zinc-200 truncate flex-1">{attachment.filename}</p>
                  <button onClick={() => setAttachment(null)} className="text-zinc-400 hover:text-white" data-testid="staff-attachment-clear">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="border-t border-white/10 p-2 flex items-end gap-2 bg-black/20">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={onPickFile}
                  accept="image/*,video/*,application/pdf,.doc,.docx,.txt,.csv"
                  className="hidden"
                  data-testid="staff-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="h-10 w-10 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 grid place-items-center shrink-0 disabled:opacity-50"
                  data-testid="staff-attach-btn"
                  title="Attach a file"
                >
                  {uploading ? (
                    <span className="w-4 h-4 rounded-full border-2 border-pink-400/40 border-t-pink-400 animate-spin" />
                  ) : (
                    <ImagePlus className="w-4 h-4 text-pink-300" />
                  )}
                </button>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder={`Message #${activeChannel.name}`}
                  rows={1}
                  className="min-h-[40px] max-h-32 resize-none bg-white/5 border-white/10 text-sm rounded-xl"
                  data-testid="staff-msg-input"
                />
                <Button
                  onClick={send}
                  disabled={!body.trim() && !attachment}
                  className="rounded-xl bg-pink-500 hover:bg-pink-400 text-white shrink-0"
                  data-testid="staff-msg-send"
                >
                  <SendIcon className="w-4 h-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="grid place-items-center h-full text-zinc-500 text-sm">No channel selected</div>
          )}
        </div>
      </div>
    </div>
  );
}
